import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/apiClient";
import { LevelBadge, TIER_PERKS } from "@/components/LevelBadge";
import { X } from "lucide-react";

export function LevelUpModal({ levelKey, onClose }) {
  useEffect(() => {
    if (levelKey) api.post("/supporter/celebrate").catch(() => {});
  }, [levelKey]);

  const name = { silver: "Silver", gold: "Gold", platinum: "Platinum" }[levelKey] || "Supporter";
  const perks = TIER_PERKS[levelKey] || [];

  return (
    <AnimatePresence>
      {levelKey && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-6" data-testid="levelup-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ scale: 0.9, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="relative bg-navy text-bone grain w-full max-w-lg border border-gold/40 shadow-2xl">
            <button onClick={onClose} data-testid="levelup-close" className="absolute top-4 right-4 text-bone/60 hover:text-gold transition-colors"><X size={20} /></button>
            <div className="p-10 text-center">
              <motion.span initial={{ letterSpacing: "0.1em", opacity: 0 }} animate={{ letterSpacing: "0.3em", opacity: 1 }} transition={{ delay: 0.15 }}
                className="font-mono text-xs uppercase tracking-[0.3em] text-gold block">You've Reached</motion.span>
              <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.3, type: "spring", stiffness: 200 }} className="mt-6 flex justify-center">
                <LevelBadge level={levelKey} size="lg" />
              </motion.div>
              <motion.h2 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
                className="heading-light font-serif text-4xl tracking-tight mt-6">{name} Supporter</motion.h2>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
                className="font-sans text-bone/70 mt-3">Your faithful partnership just unlocked new rewards.</motion.p>
              <div className="mt-8 space-y-3 text-left max-w-xs mx-auto">
                {perks.map((p, i) => (
                  <motion.div key={p} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 + i * 0.1 }}
                    className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 bg-gold shrink-0" />
                    <span className="font-sans text-sm text-bone/85">{p}</span>
                  </motion.div>
                ))}
              </div>
              <button onClick={onClose} data-testid="levelup-continue"
                className="mt-9 bg-gold text-navy px-10 py-3.5 font-mono text-xs uppercase tracking-widest hover:bg-bone transition-colors">
                Continue
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
